import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Bell, LayoutGrid } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import { TodoPanel } from "./TodoPanel";
import { ApprovalQueue } from "./ApprovalQueue";
import { WidgetPicker, type WidgetChoice } from "./WidgetPicker";
import GridLayout, { WidthProvider, type Layout } from "react-grid-layout";
import { HomeBlock } from "./HomeBlock";
import { COLUMNS, ROW_PX, MIN_W, MIN_H, useHomeLayout, type BlockSize } from "./useHomeLayout";
import { LeaveCalendar, type CalendarSize, type CalendarView } from "../leave/LeaveCalendar";
import { mailbox } from "../../api/mailbox";
import type { MailMessage, NotificationItem } from "../../api/types";

// 首頁(業主 2026-09-17)。
//
// > 每個人進到系統後 默認進入首頁
// > 首頁會有 日歷/通知列/信箱收件夾(這先假裝刻一個)/代辦事項
//
// 第二輪他把它改成更自由的一版:
//
// > 我希望首頁會是有一個日曆 (跟請假系統一樣)
// > 代辦事項 區塊太複雜了 我要做的/交辦出去的 都在一起 用顏色區分
// > 不要叫做我接下來的檔期 就是日歷 就好
// > 每個區塊 都要可以讓我們自由的拖拉大小長寬
// > 日歷做成3種大小 大中小 / 也保留目前的顯示方式 格/列
// > 額外新增 時鐘功能 上下班打卡功能(老闆有總表可以看所有人的勤務狀態)
//
// 第三輪他要的是手機桌面那種自由度:
//
// > 我覺得首頁的拖拉 做得不夠精緻 你參考iphone 或者是安卓小工具那種作法好不好
// > 把整個首頁畫成很多小小的格子可以調整每一個工具的寬高
// > 除了日曆 我想要其他工具都可以自由的托放位置/大小
//
// ==========================================================================
// 十二欄的細格子,用 react-grid-layout
// ==========================================================================
// 業主說「你自己去github上 或者其他UI/UX網頁上找看看有沒有更好得做法」——
// 這一塊(可拖曳、可縮放、會自己讓位的網格)是個有十年歷史的老問題,
// 而 react-grid-layout 是那個領域裡最成熟的一個。自己刻一份會在
// 「兩塊重疊時誰讓誰」這一題上卡很久,而那正是它處理得最好的部分。
//
// **日曆是釘住的**(業主指名的例外):它是首頁的主角,也是唯一一塊高度
// 不能隨便壓的東西 —— 一張被壓成三行的月曆等於沒有月曆。
//
// 把手只在每一塊頂端那一條上。整塊都能拖的話,在代辦事項裡選一段字
// 就會變成把整個區塊拖走。
//
// ==========================================================================
// 日曆就是請假頁那一張
// ==========================================================================
// 不是「檔期清單」的另一種畫法 —— 業主要的就是那張月曆(專案檔期與同仁休假
// 疊在一起)。同一個元件、同一份資料,兩個地方看到的東西一定一樣;
// 各做一份的話,遲早有一邊漏畫一種東西,而沒有人看得出是哪一邊錯了。
//
// ==========================================================================
// 總表**只有老闆去問**
// ==========================================================================
// 同仁的首頁根本不發那一支請求。發了只會拿到 403,而那會在每個人的
// 主控台裡留下一行紅字 —— 久了就沒有人會去看主控台。
//
// ==========================================================================
// 信箱那一塊讀本人信箱的系統備份(個人信箱第二階段)
// ==========================================================================
// 收件匣最新幾封。沒啟用就說沒啟用、給一個去啟用的入口;讀不到就說讀不到 ——
// 「沒有信」與「不知道有沒有信」是兩件事。

/** 首頁每一塊最多列幾筆。首頁是「我今天要做什麼」,不是完整清單。 */
const PREVIEW = 5;

/**
 * 預設版面。`x`/`w` 是欄(共十二欄),`y`/`h` 是列(一列 40px)。
 *
 * 這一份同時是**認得的塊的名單**:後端存著的版面裡有不認得的 id 就丟掉
 * (那是舊版本留下的),這裡新增的塊會自動補進去。見 `normalize()`。
 */
const DEFAULT_BLOCKS: BlockSize[] = [
  // 日曆一開始擺在左上角,佔三分之二寬 —— 它是首頁的主角,但**拖得動也拉得動**
  // (業主 2026-09-17:「日歷大小無法調整 修正」)。
  // `h` 與 `CALENDAR_ROWS.md` 對齊:選「中」時高度就是這一個。
  { id: "calendar", x: 0, y: 0, w: 8, h: 14, size: "md", view: "grid" },
  // 「時鐘與打卡」原本在通知上面那一格(業主 2026-09-25 拔掉了:打卡搬到門口的
  // 打卡機),通知往上補滿那一整欄。舊版面裡還留著 `clock` 的,normalize() 會略過。
  { id: "notifications", x: 8, y: 0, w: 4, h: 14 },
  { id: "todos", x: 0, y: 14, w: 8, h: 9 },
  { id: "mailbox", x: 8, y: 14, w: 4, h: 9 },
];

const OWNER_BLOCKS: BlockSize[] = [
  ...DEFAULT_BLOCKS,
  // 老闆專屬(業主 2026-09-17)。同仁的首頁連問都不去問那支 API。
  // 「出勤總表」原本在它右邊,2026-09-25 拔掉了 —— 老闆看出勤走左側的「出勤紀錄」。
  { id: "approvals", x: 0, y: 23, w: 12, h: 8 },
];

/**
 * 量寬度的那一層。
 *
 * `WidthProvider` 會在掛載與視窗大小改變時量一次外框的寬度 ——
 * 少了它,格子得自己收到一個寫死的像素寬,而首頁在筆電與外接螢幕上
 * 差了一倍。**在元件外面呼叫**:寫在 render 裡的話,每一次重繪都會
 * 產生一個新的元件型別,整棵子樹跟著重新掛載(輸入框會失焦、捲軸會跳)。
 */
const Grid = WidthProvider(GridLayout);

/**
 * 選了大 / 中 / 小之後,日曆那一塊的高度**跳到**幾列(一列 40px)。
 *
 * 「大」的意思是「資訊最清楚」,而塞在一個矮格子裡的大月曆只會開始捲 ——
 * 那正是「大」最不該有的樣子。所以選大小時順便給一個合理的高度。
 *
 * **這不是上限。** 選完之後他照樣拖得動、拉得動(業主:「日歷大小無法調整
 * 修正」),這只是一個起點。
 */
const CALENDAR_ROWS: Record<CalendarSize, number> = { sm: 10, md: 14, lg: 19 };

/**
 * 每一塊叫什麼、做什麼用的。
 *
 * 說明是給「加一個小工具」那份清單看的 —— 只列名字的話,
 * 「待我核准」與「代辦事項」看起來是同一件事。
 */
const BLOCK_INFO: Record<string, { title: string; hint: string }> = {
  calendar: { title: "日曆", hint: "專案檔期與同仁休假疊在同一張月曆上。" },
  todos: { title: "代辦事項", hint: "我要做的與我交辦出去的,同一份清單。" },
  notifications: { title: "通知", hint: "最近發生了什麼。" },
  mailbox: { title: "信箱收件夾", hint: "我的信箱收件匣最新的幾封。" },
  approvals: { title: "待我核准", hint: "請假與開票申請,等著你點頭的那幾件。" },
};

function Card({
  title,
  icon,
  to,
  more,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  to?: string;
  more?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="flex min-w-0 flex-1 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-2 text-base font-semibold text-[var(--text)]">
          {icon}
          {title}
        </h2>
        {to ? (
          <Link
            to={to}
            className="text-sm text-[var(--accent-hover)] underline-offset-2 hover:underline"
          >
            {more ?? "全部"}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function HomePage() {
  const { user } = useAuth();
  const [notes, setNotes] = useState<NotificationItem[]>([]);
  const [picking, setPicking] = useState(false);

  const owner = user?.role === "OWNER";
  const defaults = useMemo(() => (owner ? OWNER_BLOCKS : DEFAULT_BLOCKS), [owner]);
  const { blocks, update, resize, place } = useHomeLayout(defaults);

  useEffect(() => {
    api
      .get<NotificationItem[]>("/notifications")
      // 形狀要擋:拿到不是陣列的東西直接 `.map()` 會在 render 當場丟例外,
      // 而 React 會把**整棵樹**卸載 —— 整個首頁變白畫面。
      .then((got) => setNotes(Array.isArray(got) ? got : []))
      .catch(() => setNotes([]));
  }, []);

  // 信箱收件夾:null = 還在問;"off" = 沒啟用;"error" = 讀不到
  const [inbox, setInbox] = useState<MailMessage[] | "off" | "error" | null>(null);
  useEffect(() => {
    let alive = true;
    mailbox
      .status()
      .then(async (got) => {
        if (!got?.enabled) {
          if (alive) setInbox("off");
          return;
        }
        const page = await mailbox.messages({ folder: "INBOX" });
        if (alive) setInbox(Array.isArray(page?.items) ? page.items.slice(0, PREVIEW) : []);
      })
      .catch(() => {
        if (alive) setInbox("error");
      });
    return () => {
      alive = false;
    };
  }, []);

  // 關起來的那幾塊不畫,也不在版面裡佔位。
  const shown = blocks.filter((one) => one.hidden !== true);

  /** 「加一個小工具」那份清單:**每一塊都列**,關起來的沒有勾。 */
  const choices: WidgetChoice[] = blocks.map((one) => ({
    id: one.id,
    title: BLOCK_INFO[one.id]?.title ?? one.id,
    hint: BLOCK_INFO[one.id]?.hint ?? "",
    shown: one.hidden !== true,
  }));

  /** 給版面元件的那一份。 */
  const layout: Layout[] = shown.map((one) => ({
    i: one.id,
    x: one.x,
    y: one.y,
    w: one.w,
    h: one.h,
    minW: MIN_W,
    minH: MIN_H,
  }));

  const onLayoutChange = (next: Layout[]) => {
    place(next.map((one) => ({ id: one.i, x: one.x, y: one.y, w: one.w, h: one.h })));
  };

  const body = (id: string) => {
    switch (id) {
      case "calendar":
        return (
          <LeaveCalendar
            className="flex-1"
            fill
            // 業主:「不要叫做我接下來的檔期 就是日歷 就好」。
            label="日曆"
            size={(blocks.find((one) => one.id === "calendar")?.size as CalendarSize) ?? "md"}
            view={(blocks.find((one) => one.id === "calendar")?.view as CalendarView) ?? "grid"}
            // 換大小時順便把高度調到那個大小的合理起點 —— 見 `CALENDAR_ROWS`。
            onSizeChange={(size) => update("calendar", { size, h: CALENDAR_ROWS[size] })}
            onViewChange={(view) => update("calendar", { view })}
          />
        );
      case "todos":
        return <TodoPanel />;
      case "approvals":
        return <ApprovalQueue />;
      case "notifications":
        return (
          <Card title="通知" icon={<Bell aria-hidden="true" size={16} />}>
            {notes.length === 0 ? (
              <p className="m-0 text-sm text-[var(--text-3)]">目前沒有通知。</p>
            ) : (
              <ul aria-label="通知清單" className="m-0 flex flex-col gap-1 p-0">
                {notes.slice(0, PREVIEW).map((one) => (
                  <li
                    key={one.id}
                    className="flex list-none flex-wrap items-center gap-2 border-t border-[var(--border)] py-1.5 text-sm first:border-t-0"
                  >
                    {/* 未讀一律「顏色 + 文字」,不可只靠顏色。 */}
                    {one.read_at === null ? <Badge tone="accent">未讀</Badge> : null}
                    <span className="min-w-0 flex-1 truncate text-[var(--text)]">{one.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      case "mailbox":
        return (
          <Card
            title="信箱收件夾"
            icon={<Inbox aria-hidden="true" size={16} />}
            to="/mailbox"
            more="開信箱"
          >
            {inbox === null ? (
              <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
            ) : inbox === "off" ? (
              <p className="m-0 text-sm text-[var(--text-3)]">
                還沒啟用信箱。<Link to="/mailbox">到信箱輸入密碼</Link>就能在這裡看到新信。
              </p>
            ) : inbox === "error" ? (
              <p className="m-0 text-sm text-[var(--text-3)]">讀不到信箱,請稍後再試。</p>
            ) : inbox.length === 0 ? (
              <p className="m-0 text-sm text-[var(--text-3)]">收件匣是空的。</p>
            ) : (
              <ul aria-label="收件夾" className="m-0 flex flex-col gap-1 p-0">
                {inbox.map((one) => (
                  <li
                    key={one.id}
                    className="flex list-none flex-wrap items-center gap-2 border-t border-[var(--border)] py-1.5 text-sm first:border-t-0"
                  >
                    {!one.seen && !one.deleted_on_server ? <Badge tone="accent">未讀</Badge> : null}
                    {one.deleted_on_server ? <Badge tone="neutral">雲端已刪除</Badge> : null}
                    <span className="min-w-0 flex-1 truncate text-[var(--text)]">{one.subject || "(無主旨)"}</span>
                    <span className="text-xs text-[var(--text-3)]">{one.from_name || one.from_addr}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[90rem] flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h1 className="m-0 text-xl font-semibold text-[var(--text)]">
            {user?.name ? `${user.name},今天好` : "今天好"}
          </h1>
        </div>
        <Button
          icon={<LayoutGrid aria-hidden="true" size={16} />}
          onClick={() => setPicking(true)}
        >
          加一個小工具
        </Button>
      </div>

      <WidgetPicker
        open={picking}
        choices={choices}
        onToggle={(id, next) => update(id, { hidden: !next })}
        onClose={() => setPicking(false)}
      />

      {/* 十二欄的細格子。每一塊自己記著 x / y / w / h,拖到哪就在哪。 */}
      <Grid
        className="nq-home-grid"
        cols={COLUMNS}
        rowHeight={ROW_PX}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        layout={layout}
        onLayoutChange={onLayoutChange}
        // 把手只在每一塊頂端那一條上 —— 見檔頭。
        draggableHandle=".nq-drag"
        // 兩塊重疊時往上收。留空洞的話,刪掉一塊之後首頁會出現一個
        // 沒有人知道要怎麼補起來的洞。
        compactType="vertical"
        // 拖曳時不要把整頁一起捲走。
        preventCollision={false}
      >
        {shown.map((block) => (
          <HomeBlock
            key={block.id}
            id={block.id}
            title={BLOCK_INFO[block.id]?.title ?? block.id}
            onResize={(dw, dh) => resize(block.id, dw, dh)}
          >
            {body(block.id)}
          </HomeBlock>
        ))}
      </Grid>
    </div>
  );
}
